import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { proxyCoverUrl } from "../utils/coverProxy";
import {
  BookOpen, Search, ArrowRight, Star, Send, MessageCircle,
  Reply, Feather, Sparkles, Brain, Briefcase, Crown, Coffee,
  BookMarked, Users, Lightbulb, Quote, ChevronRight, Mail, ChevronDown, Menu, X,
  Target, Globe, Layers, Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Helmet } from "react-helmet-async";
import REFLECTIONS from "../data/reflections";
import { toast } from "sonner";
import { ReadersCorner } from "../components/ReadersCorner";
import { SearchSpotlight } from "../components/SearchSpotlight";

// Theme categories for "Explore by Theme" — 5 homepage themes
const THEMES = [
  { name: "Thinking & Mindset", desc: "Books that challenge how you think", icon: Brain, img: "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=600&q=80" },
  { name: "Career & Growth", desc: "Build skills. Gain clarity. Grow forward", icon: Briefcase, img: "https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?w=600&q=80" },
  { name: "Leadership & Influence", desc: "Inspire others. Lead with purpose", icon: Crown, img: "https://images.unsplash.com/photo-1727572337756-7059053381e5?w=600&q=80" },
  { name: "Life, Reflection & Culture", desc: "Explore life, philosophy, and cultural wisdom", icon: Coffee, img: "https://images.unsplash.com/photo-1614447912052-0db13d71b82e?w=600&q=80" },
  { name: "Stories & Young Minds", desc: "Enter stories, imagination, and timeless adventures", icon: Sparkles, img: "https://images.unsplash.com/photo-1532789339108-2ebc484efbf1?w=600&q=80" },
];

const BOOK_TABS = ["All Books", "Recently Added", "Most Reflected", "Short Reads"];

export default function HomePage() {
  const [featuredBooks, setFeaturedBooks] = useState([]);
  const [allBooks, setAllBooks] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0 });
  const [activeBookTab, setActiveBookTab] = useState("All Books");

  // Modal states
  const [showShareModal, setShowShareModal] = useState(false);
  const [showNextReadModal, setShowNextReadModal] = useState(false);
  const [userStory, setUserStory] = useState({ name: "", story: "" });
  const [submittingStory, setSubmittingStory] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submittingContact, setSubmittingContact] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [newComment, setNewComment] = useState({ name: "", message: "" });
  const [replyingTo, setReplyingTo] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [allBlogPosts, setAllBlogPosts] = useState([]);
  const [activeBlogCat, setActiveBlogCat] = useState("All");
  const [replyText, setReplyText] = useState({ name: "", message: "" });
  const [submittingComment, setSubmittingComment] = useState(false);
  const [nextReads, setNextReads] = useState([]);
  const [nextReadForm, setNextReadForm] = useState({ name: "", book_title: "", note: "" });
  const [submittingNextRead, setSubmittingNextRead] = useState(false);
  const [showAllConvos, setShowAllConvos] = useState(false);
  const [qualityBooks, setQualityBooks] = useState([]);
  const [activeReflection, setActiveReflection] = useState(null);

  // Navigation state
  const [activeSection, setActiveSection] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const memoryRescueCover = REFLECTIONS[0].cover;

  // Smooth scroll to section
  const scrollToSection = useCallback((sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) {
      const navHeight = 56;
      const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
      window.scrollTo({ top, behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  }, []);

  // Intersection Observer for active section tracking
  useEffect(() => {
    const sectionIds = ["home", "reflections", "explore", "community"];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [loading]);

  // Handlers
  const handleSubmitStory = async () => {
    if (!userStory.name.trim() || !userStory.story.trim()) { toast.error("Please fill in your name and story"); return; }
    setSubmittingStory(true);
    try { await axios.post(`${API}/stories`, userStory); toast.success("Thank you for sharing your thoughts!"); setUserStory({ name: "", story: "" }); setShowShareModal(false); }
    catch { toast.error("Something went wrong."); } finally { setSubmittingStory(false); }
  };
  const handleSubmitContact = async () => {
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) { toast.error("Please fill in Name, Email, and Message."); return; }
    setSubmittingContact(true);
    try { await axios.post(`${API}/contact`, contactForm); toast.success("Message sent! Lopa will get back to you soon."); setContactForm({ name: "", email: "", phone: "", message: "" }); }
    catch { toast.error("Something went wrong."); } finally { setSubmittingContact(false); }
  };
  const fetchConversations = async () => { try { const r = await axios.get(`${API}/conversations`); setConversations(r.data); } catch {} };
  const fetchNextReads = async () => { try { const r = await axios.get(`${API}/next-reads`); setNextReads(r.data); } catch {} };
  const handleSubmitNextRead = async () => {
    if (!nextReadForm.name.trim() || !nextReadForm.book_title.trim()) { toast.error("Please enter your name and the book title."); return; }
    setSubmittingNextRead(true);
    try { await axios.post(`${API}/next-reads`, nextReadForm); toast.success("Thanks for sharing your recommendation!"); setNextReadForm({ name: "", book_title: "", note: "" }); setShowNextReadModal(false); fetchNextReads(); }
    catch { toast.error("Something went wrong."); } finally { setSubmittingNextRead(false); }
  };
  const handlePostComment = async () => {
    if (!newComment.name.trim() || !newComment.message.trim()) { toast.error("Please enter your name and message."); return; }
    setSubmittingComment(true);
    try { await axios.post(`${API}/conversations`, newComment); toast.success("Comment posted!"); setNewComment({ name: "", message: "" }); fetchConversations(); }
    catch { toast.error("Failed to post."); } finally { setSubmittingComment(false); }
  };
  const handlePostReply = async (convoId) => {
    if (!replyText.name.trim() || !replyText.message.trim()) { toast.error("Please enter your name and reply."); return; }
    try { await axios.post(`${API}/conversations/${convoId}/reply`, replyText); toast.success("Reply posted!"); setReplyText({ name: "", message: "" }); setReplyingTo(null); fetchConversations(); }
    catch { toast.error("Failed to reply."); }
  };

  useEffect(() => {
    const fetchWithRetry = async (fn, retries = 3, delay = 1500) => {
      for (let i = 0; i < retries; i++) { try { return await fn(); } catch (err) { if (i === retries - 1) throw err; await new Promise(r => setTimeout(r, delay)); } }
    };
    const fetchData = async () => {
      try {
        const [featuredRes, booksRes, qualityRes] = await fetchWithRetry(() => Promise.all([
          axios.get(`${API}/books/featured`),
          axios.get(`${API}/books?limit=200&skip=0`),
          axios.get(`${API}/books?limit=200&quality_covers=true`)
        ]));
        setFeaturedBooks(featuredRes.data);
        setAllBooks(booksRes.data.books || []);
        setQualityBooks(qualityRes.data.books || []);
        setStats({ total: booksRes.data.total });
        const total = booksRes.data.total;
        if (total > 0) { const a = await axios.get(`${API}/books?limit=15&skip=${Math.max(total - 15, 0)}`); setNewArrivals((a.data.books || []).reverse()); }
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    axios.post(`${API}/track-visit`).catch(() => {});
    axios.get(`${API}/blog`).then(r => { setAllBlogPosts(r.data); setBlogPosts(r.data.slice(0, 6)); }).catch(() => {});
    fetchConversations(); fetchNextReads(); fetchData();
  }, []);

  // Books to display based on active tab
  const getWeeklyRotation = (books, count = 15) => {
    if (books.length <= count) return books.slice(0, count);
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const weekNum = Math.ceil(((now - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
    const seed = weekNum + now.getFullYear() * 100;
    let s = seed;
    const rng = () => { s = (s * 1103515245 + 12345) & 0x7fffffff; return s / 0x7fffffff; };
    const shuffled = [...books];
    for (let i = shuffled.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]; }
    const categories = [...new Set(shuffled.map(b => b.category).filter(Boolean))];
    const selected = [];
    const used = new Set();
    let round = 0;
    while (selected.length < count && round < categories.length * 4) {
      const cat = categories[round % categories.length];
      const book = shuffled.find(b => b.category === cat && !used.has(b.title));
      if (book) { selected.push(book); used.add(book.title); }
      round++;
    }
    if (selected.length < count) {
      for (const b of shuffled) { if (!used.has(b.title)) { selected.push(b); used.add(b.title); if (selected.length >= count) break; } }
    }
    return selected.slice(0, count);
  };

  const getDisplayedBooks = () => {
    switch (activeBookTab) {
      case "Recently Added": return newArrivals.slice(0, 15);
      case "Most Reflected": return featuredBooks.slice(0, 15);
      case "Short Reads": return allBooks.filter(b => b.category === "Self-Help" || b.category === "Spirituality").slice(0, 15);
      default: return getWeeklyRotation(qualityBooks.length > 0 ? qualityBooks : allBooks, 15);
    }
  };

  const insights = {
    "Fiction": "Stories that spark imagination.",
    "General": "Ideas across every domain.",
    "Bengali": "Rich literary tradition.",
    "Business & Leadership": "Lead with clarity and vision.",
    "Business & Strategy": "Think sharper. Grow faster.",
    "Self-Help": "Tiny changes, remarkable results.",
    "Spirituality": "Find meaning and peace.",
    "Psychology": "Understand the human mind.",
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)', fontFamily: "'Source Sans 3', sans-serif" }}>

      <Helmet>
        <title>The Reading Room | Curated Books, Reflections & Ideas</title>
        <meta name="description" content="A curated collection of 350+ books across leadership, mindset, culture, career growth and stories. Explore thoughtful reflections, discover your next great read, and join a community of curious minds." />
        <link rel="canonical" href="https://thereading-room.com/" />
        <meta property="og:title" content="The Reading Room | Curated Books, Reflections & Ideas" />
        <meta property="og:description" content="A curated collection of 350+ books. Explore reflections and discover your next great read." />
        <meta property="og:url" content="https://thereading-room.com/" />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* ═══ NAVIGATION ═══ */}
      <nav className="sticky top-0 z-50" style={{ background: 'var(--hero)', borderBottom: '1px solid var(--highlight-border-light)' }}>
        <div className="max-w-[1320px] mx-auto px-5 md:px-10 py-3 flex items-center justify-between">
          <button onClick={() => scrollToSection('home')} className="flex items-center gap-2.5 cursor-pointer" data-testid="nav-logo">
            <BookOpen className="w-5 h-5" style={{ color: 'var(--highlight)' }} strokeWidth={1.5} />
            <span className="text-sm font-bold tracking-wider" style={{ color: 'var(--on-hero)', fontFamily: "'Playfair Display', serif" }}>The Reading Room</span>
          </button>
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-7">
            {[
              { label: "Home", id: "home" },
              { label: "Explore", id: "explore" },
              { label: "Reflections", id: "reflections" },
              { label: "Community", id: "community" },
            ].map(item => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.id)}
                className="text-[13px] font-medium tracking-wide transition-colors relative pb-0.5"
                style={{ color: activeSection === item.id ? 'var(--highlight)' : 'var(--on-hero-muted)' }}
                data-testid={`nav-${item.id}`}
              >
                {item.label}
                {activeSection === item.id && (
                  <span className="absolute bottom-0 left-0 right-0 h-[2px] rounded-full" style={{ background: 'var(--highlight)' }} />
                )}
              </button>
            ))}
            <button
              onClick={() => setShowContactModal(true)}
              className="text-[13px] font-medium tracking-wide transition-colors relative pb-0.5"
              style={{ color: 'var(--on-hero-muted)' }}
              data-testid="nav-contact"
            >
              Contact
            </button>
          </div>
          <div className="flex items-center gap-3">
            <SearchSpotlight variant="desktop" />
            {/* Mobile hamburger */}
            <button
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg"
              style={{ background: 'rgba(255,246,230,0.08)' }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="nav-mobile-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" style={{ color: 'var(--on-hero)' }} /> : <Menu className="w-5 h-5" style={{ color: 'var(--on-hero)' }} />}
            </button>
          </div>
        </div>
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden px-5 pb-4 space-y-1" style={{ background: 'var(--hero)', borderTop: '1px solid var(--highlight-border-light)' }} data-testid="nav-mobile-menu">
            {[
              { label: "Home", id: "home" },
              { label: "Explore", id: "explore" },
              { label: "Reflections", id: "reflections" },
              { label: "Community", id: "community" },
            ].map(item => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.id)}
                className="block w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
                style={{
                  color: activeSection === item.id ? 'var(--highlight)' : 'var(--on-hero-muted)',
                  background: activeSection === item.id ? 'rgba(209,168,110,0.08)' : 'transparent'
                }}
                data-testid={`nav-mobile-${item.id}`}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => { setShowContactModal(true); setMobileMenuOpen(false); }}
              className="block w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
              style={{ color: 'var(--on-hero-muted)' }}
              data-testid="nav-mobile-contact"
            >
              Contact
            </button>
            <Link to="/browse" className="block w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium" style={{ color: 'rgba(248,245,240,0.7)' }} data-testid="nav-mobile-browse">
              Browse All Books
            </Link>
          </div>
        )}
      </nav>

      {/* ═══ HERO ═══ */}
      <section id="home" className="relative overflow-hidden" style={{ background: 'var(--hero)' }} data-testid="hero-section">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1728506972831-193841eb2961?w=1400&q=80" alt="" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, var(--hero-overlay) 0%, var(--hero-overlay-mid) 50%, var(--hero-overlay-light) 100%)' }} />
        </div>
        <div className="relative z-10 max-w-[1320px] mx-auto px-5 md:px-10 pt-8 md:pt-12 pb-8 md:pb-10">
          <div className="max-w-3xl">
            {/* Seasonal badge — hidden by default, shown by theme CSS */}
            <span
              className="hidden items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-bold tracking-wide uppercase mb-4"
              style={{ background: 'var(--green-badge)', color: '#ffffff', letterSpacing: '0.08em' }}
              data-testid="hero-badge-seasonal"
            >
              Celebrating Literacy &amp; Learning
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold leading-[1.1] mb-4 whitespace-nowrap" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--on-hero)' }} data-testid="hero-headline">
              Books that Shape Thinking
            </h1>
            <p className="text-lg md:text-xl font-medium mb-6 leading-relaxed" style={{ color: '#FFFFFF' }}>
              A space to explore, reflect and grow together.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/browse">
                <button className="flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all hover:opacity-90 hover:shadow-lg" style={{ background: 'var(--highlight)', color: 'var(--text)' }} data-testid="hero-cta-button">
                  Explore Reading Room <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </button>
              </Link>
              <button onClick={() => setActiveReflection(REFLECTIONS[0])} className="flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-colors hover:bg-white/10" style={{ background: 'transparent', color: 'var(--on-hero)', border: '1px solid rgba(248,245,240,0.35)' }} data-testid="hero-reflect-btn">
                Browse Reflections
              </button>
            </div>
          </div>
          {/* Book Lovers Day — hidden by default, shown by .theme-book-lovers-day */}
          <div
            className="hidden absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 flex-col items-center"
            data-testid="book-lovers-day-illustration"
          >
            <svg width="140" height="150" viewBox="0 0 140 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg">
              {/* Casually piled stack of books, tilted ~-8deg */}
              <g transform="rotate(-8 70 95)">
                {/* Book 1 (bottom) - navy, widest offset */}
                <rect x="25" y="100" width="90" height="16" rx="2" transform="rotate(-3 70 108)" fill="#264653" opacity="0.9" />
                <rect x="35" y="104" width="30" height="3" rx="1" fill="rgba(255,255,255,0.25)" />
                {/* Book 2 - deep red, offset up-right */}
                <rect x="30" y="82" width="85" height="16" rx="2" transform="rotate(2 72 90)" fill="#C1574E" opacity="0.9" />
                <rect x="40" y="86" width="28" height="3" rx="1" fill="rgba(255,255,255,0.25)" />
                {/* Book 3 - amber, offset more */}
                <rect x="28" y="64" width="80" height="16" rx="2" transform="rotate(-4 68 72)" fill="#D8862E" opacity="0.9" />
                <rect x="38" y="68" width="32" height="3" rx="1" fill="rgba(255,255,255,0.25)" />
                {/* Book 4 (top of stack) - teal, smallest offset */}
                <rect x="35" y="47" width="75" height="15" rx="2" transform="rotate(1 72 54)" fill="#2A9D8F" opacity="0.9" />
                <rect x="44" y="51" width="26" height="3" rx="1" fill="rgba(255,255,255,0.25)" />
              </g>
              {/* Open book resting on top of the pile */}
              <g transform="translate(40, 10) rotate(-6)">
                {/* Left page */}
                <path d="M30 8 C30 8, 28 2, 15 0 C5 -1, 0 2, 0 4 L0 30 C0 30, 8 27, 15 28 C24 29, 30 32, 30 32 Z" fill="var(--bg)" stroke="var(--highlight)" strokeWidth="0.5" />
                {/* Right page */}
                <path d="M30 8 C30 8, 32 2, 45 0 C55 -1, 60 2, 60 4 L60 30 C60 30, 52 27, 45 28 C36 29, 30 32, 30 32 Z" fill="var(--bg-light)" stroke="var(--highlight)" strokeWidth="0.5" />
                {/* Page lines - left */}
                <line x1="6" y1="10" x2="24" y2="12" stroke="var(--highlight)" strokeWidth="0.4" opacity="0.4" />
                <line x1="6" y1="15" x2="24" y2="17" stroke="var(--highlight)" strokeWidth="0.4" opacity="0.4" />
                <line x1="6" y1="20" x2="24" y2="22" stroke="var(--highlight)" strokeWidth="0.4" opacity="0.4" />
                {/* Page lines - right */}
                <line x1="36" y1="12" x2="54" y2="10" stroke="var(--highlight)" strokeWidth="0.4" opacity="0.4" />
                <line x1="36" y1="17" x2="54" y2="15" stroke="var(--highlight)" strokeWidth="0.4" opacity="0.4" />
                <line x1="36" y1="22" x2="54" y2="20" stroke="var(--highlight)" strokeWidth="0.4" opacity="0.4" />
              </g>
            </svg>
            <p
              className="mt-2 text-center text-base font-semibold leading-snug"
              style={{ fontFamily: "'Playfair Display', serif", color: 'var(--accent)' }}
            >
              Celebrating Book Lovers Day
            </p>
            <div className="mt-1.5 w-36 h-px" style={{ background: 'rgba(245,166,35,0.5)' }} />
          </div>
          {/* September — International Literacy Day illustration, hidden by default */}
          <div
            className="hidden absolute right-6 lg:right-12 top-1/2 -translate-y-1/2 flex-col items-center"
            data-testid="september-literacy-illustration"
          >
            <svg width="120" height="100" viewBox="0 0 120 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg">
              {/* Open book — cream/tan pages with gold outline */}
              <g transform="translate(10, 15)">
                {/* Left page */}
                <path d="M50 12 C50 12, 47 4, 30 2 C16 0, 0 5, 0 8 L0 60 C0 60, 16 55, 30 56 C44 57, 50 62, 50 62 Z" fill="#F5F0E4" stroke="#C9A227" strokeWidth="1.2" />
                {/* Right page */}
                <path d="M50 12 C50 12, 53 4, 70 2 C84 0, 100 5, 100 8 L100 60 C100 60, 84 55, 70 56 C56 57, 50 62, 50 62 Z" fill="#FBF7EE" stroke="#C9A227" strokeWidth="1.2" />
                {/* Page lines - left */}
                <line x1="10" y1="18" x2="40" y2="21" stroke="#C9A227" strokeWidth="0.5" opacity="0.35" />
                <line x1="10" y1="26" x2="40" y2="29" stroke="#C9A227" strokeWidth="0.5" opacity="0.35" />
                <line x1="10" y1="34" x2="40" y2="37" stroke="#C9A227" strokeWidth="0.5" opacity="0.35" />
                <line x1="10" y1="42" x2="40" y2="45" stroke="#C9A227" strokeWidth="0.5" opacity="0.35" />
                {/* Page lines - right */}
                <line x1="60" y1="21" x2="90" y2="18" stroke="#C9A227" strokeWidth="0.5" opacity="0.35" />
                <line x1="60" y1="29" x2="90" y2="26" stroke="#C9A227" strokeWidth="0.5" opacity="0.35" />
                <line x1="60" y1="37" x2="90" y2="34" stroke="#C9A227" strokeWidth="0.5" opacity="0.35" />
                <line x1="60" y1="45" x2="90" y2="42" stroke="#C9A227" strokeWidth="0.5" opacity="0.35" />
                {/* Spine shadow */}
                <line x1="50" y1="12" x2="50" y2="62" stroke="#C9A227" strokeWidth="0.8" opacity="0.5" />
              </g>
            </svg>
            <p
              className="mt-2 text-center text-sm font-semibold leading-snug"
              style={{ fontFamily: "'Playfair Display', serif", color: 'var(--highlight)' }}
            >
              International Literacy Day
            </p>
            <div className="mt-1.5 w-40 h-px" style={{ background: 'rgba(245,166,35,0.5)' }} />
          </div>
        </div>
      </section>

      {/* ═══ FROM THE READING ROOM — BLOG (Card Grid) ═══ */}
      <section className="py-14 md:py-20 px-5 md:px-10" style={{ background: 'var(--bg-light)' }} data-testid="blog-section">
        <div className="max-w-[1320px] mx-auto">
          <div className="mb-8 md:mb-10">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <p className="text-[11px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: 'var(--muted)' }}>
                  From the Reading Room
                </p>
                <h2 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>
                  Curated reads for curious minds
                </h2>
              </div>
              <Link
                to="/blog"
                className="hidden sm:flex items-center gap-2 text-sm font-medium whitespace-nowrap mt-6 transition-colors hover:opacity-70"
                style={{ color: 'var(--muted2)' }}
                data-testid="blog-explore-all-top"
              >
                View all articles <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            </div>
            <p className="text-sm mb-5" style={{ color: 'var(--muted2)' }}>
              Hand-picked book lists, stories, and ideas.
            </p>
            {/* Category filter pills */}
            <div className="flex items-center gap-3 flex-wrap">
              {["All", ...Array.from(new Set(allBlogPosts.map(p => p.category).filter(Boolean)))].map(cat => (
                <button
                  key={cat}
                  onClick={() => {
                    setActiveBlogCat(cat);
                    if (cat === "All") { setBlogPosts(allBlogPosts.slice(0, 6)); }
                    else { setBlogPosts(allBlogPosts.filter(p => p.category === cat || (p.tags && p.tags.includes(cat))).slice(0, 6)); }
                  }}
                  className="px-4 py-1.5 rounded-full text-xs font-semibold transition-all"
                  style={activeBlogCat === cat
                    ? { background: 'var(--accent)', color: '#FFF' }
                    : { background: 'transparent', color: 'var(--text)', border: '1px solid var(--accent)', opacity: 0.8 }
                  }
                  data-active={activeBlogCat === cat ? "true" : undefined}
                  data-testid={`blog-cat-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {cat}
                </button>
              ))}
            </div>
            {/* Divider */}
            <div className="mt-8" style={{ borderBottom: '2px solid var(--divider)' }} />
          </div>

          {blogPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7 mt-2">
              {blogPosts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group block rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
                  style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
                  data-testid={`home-blog-card-${post.slug}`}
                >
                  {/* Image */}
                  <div className="overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      style={{ aspectRatio: '16/10' }}
                    />
                  </div>
                  {/* Text */}
                  <div className="p-5">
                    <span
                      className="text-[11px] font-bold tracking-[0.15em] uppercase mb-2 block"
                      style={{ color: 'var(--accent)' }}
                    >
                      {post.category}
                    </span>
                    <h3 className="text-[17px] font-medium leading-snug mb-2 line-clamp-2" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>
                      {post.title}
                    </h3>
                    <p className="text-[13px] leading-relaxed mb-4 line-clamp-2" style={{ color: 'var(--muted)' }}>
                      {post.excerpt}
                    </p>
                    <span
                      className="inline-flex items-center gap-1.5 text-[13px] font-semibold transition-all group-hover:opacity-70"
                      style={{ color: 'var(--accent)' }}
                    >
                      Read the reflection <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-center py-10 text-sm" style={{ color: 'var(--muted)' }}>No articles in this category yet.</p>
          )}

        </div>
      </section>

      {/* ═══ REFLECTIONS — Fixed Grid ═══ */}
      <section id="reflections" className="py-14 md:py-20 px-5 md:px-10" style={{ background: 'var(--bg-alt)' }} data-testid="reflect-section">
        <div className="max-w-[1320px] mx-auto">
          <div className="flex items-end justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>Reflections that stayed with me</h2>
            <Link to="/reflections" className="hidden sm:flex items-center gap-2 text-sm font-medium transition-colors hover:opacity-70" style={{ color: 'var(--muted2)' }} data-testid="reflections-view-all">
              View all reflections <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
            </Link>
          </div>
          <ReflectionGrid reflections={REFLECTIONS} onReadMore={(ref) => setActiveReflection(ref)} />
          <div className="sm:hidden text-center mt-6">
            <Link to="/reflections" className="inline-flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--muted2)' }}>
              View all reflections <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ NOT SURE WHAT TO READ NEXT? — SAGE BANNER ═══ */}
      <section className="py-8 px-5 md:px-10" style={{ background: 'var(--bg-alt)' }}>
        <div
          className="max-w-[1320px] mx-auto rounded-2xl md:rounded-[16px] overflow-hidden"
          style={{ background: '#DCE4D1' }}
          data-testid="discovery-banner"
        >
          <div className="flex flex-col md:flex-row items-stretch">
            {/* Text area — 58% */}
            <div className="w-full md:w-[58%] flex flex-col justify-center px-6 py-8 md:px-12 md:py-8 relative">
              {/* Decorative gold accent line */}
              <div className="hidden md:block absolute left-12 top-8 w-12 h-[2px] rounded-full" style={{ background: '#D9A441' }} />
              <h2
                className="text-[26px] sm:text-[32px] md:text-[36px] lg:text-[40px] font-bold leading-[1.12] mb-4 md:mt-6"
                style={{ fontFamily: "'Playfair Display', serif", color: '#34452C' }}
              >
                Not Sure What to Read Next?
              </h2>
              <p
                className="text-[14px] md:text-[16px] lg:text-[17px] leading-[1.55] mb-6 max-w-[540px]"
                style={{ color: '#343A30' }}
              >
                Discover books that speak to your interests, inspire new perspectives, and make your next read meaningful.
              </p>
              <button
                onClick={() => scrollToSection('explore')}
                className="inline-flex items-center gap-2 px-6 md:px-7 h-12 rounded-lg text-[15px] font-semibold transition-all hover:opacity-90 hover:shadow-md self-start"
                style={{ background: '#3D512C', color: '#FFFFFF' }}
                data-testid="discovery-banner-cta"
              >
                Explore Books by Theme <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
            {/* Image area — 42% */}
            <div className="w-full md:w-[42%] relative" style={{ minHeight: '180px' }}>
              {/* Subtle botanical leaf SVG overlay */}
              <svg className="hidden md:block absolute -left-6 top-1/2 -translate-y-1/2 z-10 opacity-20" width="48" height="120" viewBox="0 0 48 120" fill="none">
                <path d="M24 0C24 0 0 40 0 60C0 80 24 120 24 120C24 120 48 80 48 60C48 40 24 0 24 0Z" fill="#34452C" />
              </svg>
              <img
                src="https://images.pexels.com/photos/15597302/pexels-photo-15597302.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Books, coffee and greenery in warm sunlight"
                className="w-full h-full object-cover"
                style={{ minHeight: '180px', maxHeight: '320px' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ═══ EXPLORE BY THEME ═══ */}
      <section id="explore" className="py-16 md:py-20 px-5 md:px-10" style={{ background: 'var(--bg)' }}>
        <div className="max-w-[1320px] mx-auto">
          <div className="flex items-end justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>Explore by Theme</h2>
            <Link to="/browse" className="hidden sm:flex items-center gap-2 text-sm font-medium transition-colors hover:opacity-70" style={{ color: 'var(--muted2)' }} data-testid="theme-view-all">
              See all themes <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 md:gap-6">
            {THEMES.map((t, idx) => {
              // Per-card overlay and brightness for cards 4 & 5
              const overlay = idx === 3
                ? 'linear-gradient(to bottom, rgba(10,6,3,0.04) 0%, rgba(10,6,3,0.20) 100%)'
                : idx === 4
                  ? 'linear-gradient(to bottom, rgba(10,6,3,0.07) 0%, rgba(10,6,3,0.28) 100%)'
                  : 'linear-gradient(to bottom, rgba(10,6,3,0.12) 0%, rgba(10,6,3,0.38) 100%)';
              const imgBrightness = idx === 3 ? 'brightness(1.30)' : idx === 4 ? 'brightness(1.18)' : undefined;

              return (
              <Link key={t.name} to={`/browse?homepageTheme=${encodeURIComponent(t.name)}`}
                className="theme-card group block rounded-2xl overflow-hidden transition-all duration-300"
                style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
                data-testid={`theme-card-${t.name}`}
              >
                {/* Image with centered icon */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={t.img} alt={t.name} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" style={imgBrightness ? { filter: imgBrightness } : undefined} />
                  <div className="absolute inset-0" style={{ background: overlay }} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                      <t.icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                    </div>
                  </div>
                </div>
                {/* Text below image */}
                <div className="p-4 text-center">
                  <h3 className="font-bold text-sm mb-1" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>{t.name}</h3>
                  <p className="text-[11px] leading-relaxed" style={{ color: 'var(--muted)' }}>{t.desc}</p>
                </div>
              </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ BOOKS TO EXPLORE ═══ */}
      <section className="py-16 md:py-20 px-5 md:px-10" style={{ background: 'var(--bg)' }} data-testid="featured-section">
        <div className="max-w-[1320px] mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>Books to Explore</h2>
            <div className="flex items-center gap-2 flex-wrap">
              {BOOK_TABS.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveBookTab(tab)}
                  className="px-4 py-1.5 rounded-full text-xs font-semibold transition-all"
                  style={activeBookTab === tab
                    ? { background: 'var(--accent)', color: '#FFF' }
                    : { background: 'transparent', color: 'var(--text)', border: '1px solid var(--accent)', opacity: 0.8 }
                  }
                  data-active={activeBookTab === tab ? "true" : undefined}
                  data-testid={`book-tab-${tab.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {tab}
                </button>
              ))}
              <Link to="/browse" className="hidden lg:flex items-center gap-2 text-sm font-medium ml-4 transition-colors hover:opacity-70" style={{ color: 'var(--muted2)' }} data-testid="featured-view-all">
                View all books <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            </div>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
              {[...Array(10)].map((_, i) => (<div key={i} className="animate-pulse"><div className="aspect-[2/3] rounded-xl mb-3" style={{ background: 'var(--skeleton)' }} /><div className="h-3 rounded w-3/4 mb-1.5" style={{ background: 'var(--skeleton)' }} /></div>))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {getDisplayedBooks().map((book, index) => (
                <BookCard key={book.book_id} book={book} index={index} insights={insights} />
              ))}
            </div>
          )}
        </div>
      </section>

      <ReadersCorner />

      {/* ═══ FOOTER ═══ */}
      <footer className="py-12 px-5 md:px-10" style={{ background: 'var(--hero-dark)', borderTop: '1px solid var(--highlight-border-light)' }}>
        <div className="max-w-[1320px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-start gap-8 md:gap-0">
            {/* Left: Brand */}
            <div className="md:w-[28%]">
              <div className="flex items-center gap-2.5 mb-2">
                <BookOpen className="w-6 h-6" style={{ color: 'var(--highlight)' }} strokeWidth={1.5} />
                <span className="text-base font-bold tracking-wider" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--on-hero)' }}>THE READING ROOM</span>
              </div>
              <p className="text-[12px] tracking-wide" style={{ color: 'var(--on-hero-subtle)' }}>Books. Reflections. Growth.</p>
            </div>

            {/* Center: Three link columns */}
            <div className="flex flex-wrap gap-10 md:gap-14 md:w-[48%]">
              <div>
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: 'var(--on-hero-subtle)' }}>Explore</p>
                <div className="space-y-2">
                  <Link to="/browse" className="block text-[13px] transition-colors hover:text-[var(--highlight)]" style={{ color: 'var(--on-hero-muted)' }}>Books</Link>
                  <button onClick={() => scrollToSection('reflections')} className="block text-[13px] transition-colors hover:text-[var(--highlight)]" style={{ color: 'var(--on-hero-muted)' }}>Reflections</button>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: 'var(--on-hero-subtle)' }}>Connect</p>
                <div className="space-y-2">
                  <button onClick={() => scrollToSection('community')} className="block text-[13px] transition-colors hover:text-[var(--highlight)]" style={{ color: 'var(--on-hero-muted)' }}>Community</button>
                  <button onClick={() => setShowNextReadModal(true)} className="block text-[13px] transition-colors hover:text-[var(--highlight)]" style={{ color: 'var(--on-hero-muted)' }}>Submit a Book</button>
                  <button onClick={() => setShowContactModal(true)} className="block text-[13px] transition-colors hover:text-[var(--highlight)]" style={{ color: 'var(--on-hero-muted)' }}>Contact Lopa</button>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: 'var(--on-hero-subtle)' }}>About</p>
                <div className="space-y-2">
                  <button onClick={() => scrollToSection('community')} className="block text-[13px] transition-colors hover:text-[var(--highlight)]" style={{ color: 'var(--on-hero-muted)' }}>About Lopa</button>
                  <Link to="/admin/login" className="block text-[13px] transition-colors hover:text-[var(--highlight)]" style={{ color: 'var(--on-hero-muted)' }}>Admin</Link>
                </div>
              </div>
            </div>

            {/* Right: Prominent Instagram */}
            <div className="md:w-[24%] md:flex md:justify-end md:items-start md:border-l md:border-[rgba(255,255,255,0.08)] md:pl-10">
              <a
                href="https://www.instagram.com/the__reading.room/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 transition-opacity hover:opacity-80"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--on-hero-muted)' }}>
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                </svg>
                <span className="text-lg font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--on-hero-muted)' }}>Instagram</span>
                <ArrowRight className="w-4 h-4 -rotate-45" style={{ color: 'var(--on-hero-subtle)' }} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Bottom line */}
          <div className="mt-8 pt-5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="text-[10px]" style={{ color: 'var(--on-hero-subtle)' }}>Curated with love by Lopa &middot; {new Date().getFullYear()}</p>
          </div>
        </div>
      </footer>

      {/* ═══ MODALS ═══ */}

      {/* Reflection Modal — dynamic, works for any reflection */}
      <Dialog open={!!activeReflection} onOpenChange={(open) => { if (!open) setActiveReflection(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl" data-testid="reflect-modal" style={{ background: 'var(--bg-light)', border: '1px solid var(--highlight-border-light)' }}>
          {activeReflection && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>
                  Reflection on {activeReflection.title}
                </DialogTitle>
              </DialogHeader>
              {/* Book header */}
              <div className="flex gap-4 items-start mt-2 mb-4 p-4 rounded-xl" style={{ background: 'var(--card-bg)' }}>
                <img src={activeReflection.cover} alt={activeReflection.title} className="w-20 h-28 object-cover rounded-lg shadow-md flex-shrink-0" />
                <div>
                  <p className="font-bold text-lg" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>{activeReflection.title}</p>
                  <p className="text-sm mt-1" style={{ color: 'var(--highlight)' }}>by {activeReflection.author}</p>
                  <span className="inline-block mt-2 px-2.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: 'var(--highlight-muted)', color: 'var(--muted)' }}>{activeReflection.theme}</span>
                </div>
              </div>
              {/* Why this book matters */}
              <div className="mb-4">
                <p className="uppercase tracking-[0.15em] text-[10px] font-bold mb-3" style={{ color: 'var(--highlight)' }}>Why this book matters</p>
                <div className="space-y-3 text-sm leading-relaxed" style={{ color: 'var(--muted)' }}>
                  {activeReflection.fullReflection.map((p, i) => (<p key={i}>{p}</p>))}
                </div>
              </div>
              {/* Takeaways */}
              {activeReflection.takeaways.length > 0 && (
                <div className="mb-4">
                  <p className="uppercase tracking-[0.15em] text-[10px] font-bold mb-3" style={{ color: 'var(--highlight)' }}>Key Takeaways</p>
                  <div className="space-y-3">
                    {activeReflection.takeaways.map((t, i) => (
                      <div key={i} className="p-4 rounded-xl" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                        <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>{i + 1}. {t.title}</p>
                        {t.body && <p className="text-sm leading-relaxed" style={{ color: 'var(--muted)' }}>{t.body}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Closing line */}
              <div className="pt-4 mb-3" style={{ borderTop: '1px solid var(--highlight-border-light)' }}>
                <p className="text-sm font-semibold italic leading-relaxed" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text-secondary)' }}>
                  {activeReflection.closingLine}
                </p>
              </div>
              {/* Share row */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[11px] font-medium" style={{ color: 'var(--muted)' }}>Share this reflection:</span>
                <button
                  data-testid="share-whatsapp"
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-semibold transition-opacity hover:opacity-80"
                  style={{ background: '#25D366', color: '#fff' }}
                  onClick={() => {
                    const text = `"${activeReflection.closingLine}"\n\nRead my reflection on ${activeReflection.title} by ${activeReflection.author}\nhttps://thereading-room.com`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                  }}
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp
                </button>
                <button
                  data-testid="share-linkedin"
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-semibold transition-opacity hover:opacity-80"
                  style={{ background: '#0A66C2', color: '#fff' }}
                  onClick={() => {
                    const text = `"${activeReflection.closingLine}"\n\nMy reflection on ${activeReflection.title} by ${activeReflection.author}\n\nhttps://thereading-room.com`;
                    window.open(`https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`, '_blank', 'width=600,height=600');
                  }}
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  LinkedIn
                </button>
                <button className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-semibold transition-opacity hover:opacity-80" style={{ background: 'var(--hero)', color: 'var(--on-hero)' }} onClick={() => { setActiveReflection(null); setShowShareModal(true); }}>
                  <Feather className="w-3 h-3" strokeWidth={1.5} /> Share Your Thoughts
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showNextReadModal} onOpenChange={setShowNextReadModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl" data-testid="next-read-modal" style={{ background: 'var(--bg-light)', border: '1px solid var(--highlight-border)' }}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>What's Your Next Read?</DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: 'var(--muted)' }}>Share the book you're planning to read or recommend a title you loved.</p>
          {nextReads.length > 0 && (
            <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
              {nextReads.map((nr) => (
                <div key={nr.read_id} className="p-3 rounded-lg" style={{ background: 'var(--card-bg)', border: '1px solid var(--highlight-border-light)' }}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold" style={{ background: nr.is_admin ? 'var(--highlight)' : 'var(--muted)', color: 'var(--card-bg)' }}>{nr.name.charAt(0).toUpperCase()}</div>
                    <span className="text-xs font-semibold" style={{ color: 'var(--text)' }}>{nr.name}</span>
                    {nr.is_admin && <span className="px-1 py-0.5 rounded text-[7px] font-bold" style={{ background: 'var(--highlight-muted)', color: 'var(--highlight)' }}>LOPA</span>}
                  </div>
                  <p className="text-xs font-bold ml-8" style={{ color: 'var(--text)' }}>{nr.book_title}</p>
                  {nr.note && <p className="text-[10px] ml-8 mt-0.5" style={{ color: 'var(--muted)' }}>{nr.note}</p>}
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 pt-3 space-y-3" style={{ borderTop: '1px solid var(--highlight-border-light)' }}>
            <Input data-testid="next-read-name" placeholder="Your Name" value={nextReadForm.name} onChange={(e) => setNextReadForm({ ...nextReadForm, name: e.target.value })} className="rounded-lg text-sm" style={{ background: 'var(--card-bg)', borderColor: 'var(--highlight-border)' }} />
            <Input data-testid="next-read-title" placeholder="Book title..." value={nextReadForm.book_title} onChange={(e) => setNextReadForm({ ...nextReadForm, book_title: e.target.value })} className="rounded-lg text-sm" style={{ background: 'var(--card-bg)', borderColor: 'var(--highlight-border)' }} />
            <Textarea data-testid="next-read-note" placeholder="Why this book? (optional)" value={nextReadForm.note} onChange={(e) => setNextReadForm({ ...nextReadForm, note: e.target.value })} rows={2} className="rounded-lg text-sm" style={{ background: 'var(--card-bg)', borderColor: 'var(--highlight-border)' }} />
            <button data-testid="next-read-submit" className="w-full py-2.5 rounded-lg text-xs font-semibold transition-opacity hover:opacity-90" style={{ background: 'var(--highlight)', color: 'var(--text)' }} onClick={handleSubmitNextRead} disabled={submittingNextRead}>
              {submittingNextRead ? "Sharing..." : "Share Your Pick"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="max-w-lg rounded-xl" data-testid="share-modal" style={{ background: 'var(--bg-light)', border: '1px solid var(--highlight-border)' }}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>Share Your Thoughts</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Input data-testid="share-story-name" placeholder="Your Name" value={userStory.name} onChange={(e) => setUserStory({ ...userStory, name: e.target.value })} className="rounded-lg text-sm" style={{ background: 'var(--card-bg)', borderColor: 'var(--highlight-border)' }} />
            <Textarea data-testid="share-story-text" placeholder="Share your reading experience..." value={userStory.story} onChange={(e) => setUserStory({ ...userStory, story: e.target.value })} rows={4} className="rounded-lg text-sm" style={{ background: 'var(--card-bg)', borderColor: 'var(--highlight-border)' }} />
            <button data-testid="share-story-submit" className="w-full py-2.5 rounded-lg text-xs font-semibold transition-opacity hover:opacity-90" style={{ background: 'var(--highlight)', color: 'var(--text)' }} onClick={handleSubmitStory} disabled={submittingStory}>
              {submittingStory ? "Submitting..." : "Submit Feedback"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Lopa Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="max-w-lg rounded-xl" data-testid="contact-modal" style={{ background: 'var(--bg-light)', border: '1px solid var(--highlight-border)' }}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>Connect with Lopa</DialogTitle>
          </DialogHeader>
          <p className="text-sm mb-2" style={{ color: 'var(--muted)' }}>Have a question, suggestion, or just want to say hello?</p>
          <div className="space-y-3 mt-2">
            <Input data-testid="contact-name" placeholder="Your name" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} className="h-11 text-sm rounded-lg" style={{ background: 'var(--card-bg)', borderColor: 'var(--highlight-border)', color: 'var(--text)' }} />
            <Input data-testid="contact-email" type="email" placeholder="Email address" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className="h-11 text-sm rounded-lg" style={{ background: 'var(--card-bg)', borderColor: 'var(--highlight-border)', color: 'var(--text)' }} />
            <Textarea data-testid="contact-message" placeholder="Your message..." value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} rows={3} className="text-sm rounded-lg" style={{ background: 'var(--card-bg)', borderColor: 'var(--highlight-border)', color: 'var(--text)' }} />
            <button data-testid="contact-submit" className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90" style={{ background: 'var(--accent)', color: '#FFF' }} onClick={() => { handleSubmitContact(); setShowContactModal(false); }} disabled={submittingContact}>
              {submittingContact ? "Sending..." : "Send Message"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Book Card — Clean white card with cover, title, insight, category badge
const BookCard = ({ book, insights }) => {
  const [err, setErr] = useState(false);
  const show = !book.cover_image || book.cover_image.includes('placehold') || err;
  const colors = ["from-amber-900 to-amber-700", "from-stone-800 to-stone-600", "from-emerald-900 to-emerald-700", "from-yellow-900 to-yellow-700"];
  const c = book.title.length % colors.length;

  const categoryColors = {
    "Self-Help": { bg: '#E8DCC8', text: '#5D4A32' },
    "Business & Strategy": { bg: '#D4E8D4', text: '#2D5A2D' },
    "Business & Leadership": { bg: '#D4D4E8', text: '#3A3A6B' },
    "Spirituality": { bg: '#E8D4D4', text: '#6B3A3A' },
    "Fiction": { bg: '#D4E0E8', text: '#2D4A5A' },
    "Psychology": { bg: '#E8E0D4', text: '#5A4A2D' },
    "Bengali": { bg: '#E0D4E8', text: '#4A2D5A' },
    "General": { bg: '#E0E0E0', text: '#4A4A4A' },
  };
  const catColor = categoryColors[book.category] || { bg: '#E8DCC8', text: '#5D4A32' };

  return (
    <Link to={`/book/${book.book_id}`} className="book-card group block" data-testid={`book-card-${book.book_id}`}>
      <div className="rounded-xl overflow-hidden transition-all duration-300" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', boxShadow: 'var(--card-shadow)' }}>
        {/* Cover */}
        <div className="aspect-[2/3] overflow-hidden">
          {show ? (
            <div className={`w-full h-full bg-gradient-to-br ${colors[c]} flex flex-col items-center justify-center p-4 text-center`}>
              <BookOpen className="w-7 h-7 text-white/40 mb-2" strokeWidth={1.5} />
              <h4 className="text-white font-bold text-xs leading-tight line-clamp-3 mb-1">{book.title}</h4>
              <p className="text-white/70 text-[10px]">{book.author}</p>
            </div>
          ) : (
            <img src={proxyCoverUrl(book.cover_image)} alt={book.title} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" onError={() => setErr(true)} />
          )}
        </div>
        {/* Info */}
        <div className="p-3.5">
          <h3 className="font-bold text-[16px] leading-snug line-clamp-3 mb-1" style={{ color: 'var(--text)' }}>{book.title}</h3>
          <p className="text-[12px] leading-relaxed line-clamp-1 mb-2" style={{ color: 'var(--muted)' }}>
            {insights[book.category] || "A thoughtful read."}
          </p>
          {book.category && (
            <span className="inline-block px-2.5 py-0.5 rounded text-[11px] font-semibold" style={{ background: catColor.bg, color: catColor.text }}>
              {book.category}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

// Reflection Grid — fixed grid of 5 most recent reflections
const ReflectionGrid = ({ reflections, onReadMore }) => {
  const latest = [...reflections].reverse().slice(0, 5);

  const themeColors = {
    "Thinking & Mindset": { bg: '#E8EDF5', color: '#2D3A5C' },
    "Life, Reflection & Culture": { bg: '#F0E8DC', color: 'var(--muted)' },
    "Career & Growth": { bg: '#E1ECDF', color: '#3A5A32' },
    "Leadership & Influence": { bg: '#E8E0F0', color: '#4A2D5A' },
    "Stories & Young Minds": { bg: '#FDE8D8', color: '#8B4513' },
    "Risk, Uncertainty & Decision-Making": { bg: '#F5E6E0', color: '#7A3B2E' },
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 md:gap-6">
      {latest.map((ref) => {
        const tc = themeColors[ref.theme] || { bg: '#F0E8DC', color: 'var(--muted)' };
        return (
          <div
            key={ref.id}
            className="rounded-xl cursor-pointer transition-transform duration-200 hover:-translate-y-1 flex flex-col"
            style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
            onClick={() => onReadMore(ref)}
            data-testid={`reflection-card-${ref.id}`}
          >
            <div style={{ padding: '14px 14px 0 14px' }}>
              <img
                src={ref.cover}
                alt={ref.title}
                className="w-full object-contain rounded-lg"
                style={{ aspectRatio: '2/3' }}
              />
            </div>
            <div className="flex flex-col flex-1" style={{ padding: '12px 14px 14px 14px' }}>
              <h4 className="font-bold text-[17px] leading-tight mb-1.5" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: '3.2em' }}>
                {ref.title}
              </h4>
              <p className="text-[13px] leading-snug mb-3" style={{ color: 'var(--muted)' }}>
                {ref.author}
              </p>
              <span
                className="inline-block px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide mt-auto self-start"
                style={{ background: tc.bg, color: tc.color }}
              >
                {ref.theme}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

